import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { InfoTooltip } from './InfoTooltip';

// ─── SecondaryAxisPills ─────────────────────────────────────
// <purpose>THE rendering of the secondary classification axis — Grund-Code (gMSB/wMSB), Anliegen
// (VNB), Prozesstyp (WIM). One component so the convention cannot drift, ported here from the
// three-tier fallback that used to live inline in TicketRowMeta and let a vocabulary delivery
// diverge the face from the ruling below. Consumers today: TicketRowMeta, DashboardSeite (Verlauf
// hover tooltip + Details panel), TicketDetail, StatistikSeite (reason-code rows), and formerly the triage panel
// (process-type chip, deleted part 6 — this migration also ended the one surface that used to render the FULL
// label with no overflow handling, because it was written separately from every other surface),
// and ChatbotTestSeite (per-message chip row + Letzte-Antwort panel — both were plain display, so
// they adopted this component rather than merely reusing its accessors, once inspection showed
// neither carried a click or active-state affordance). UploadsSeite still calls
// secondaryAxisText/secondaryAxisHover directly (not this component) for its own INTERACTIVE
// (removable, ×-bearing) reason-code chip, same boundary FilterPanel's secondary-axis filter
// bubble draws (own markup/active-state, resolution accessors only, see its own comment).</purpose>
// <dependencies>useCategoryMeta — secondaryAxisText / secondaryAxisHover are the only resolution
// source; this component decides presentation, never wording.</dependencies>
// <constraints>The ruling, fleet-wide with NO role exceptions: the RESOLVED label as the face,
// rendered VERBATIM by the `axis2` register (part 7, 2026-10-11: the served text is never recased —
// lowercasing it had turned „TAB/RFZ" into „tab/rfz", an operator-ruled defect; the register keeps
// the gray look of 2026-09-08, walk round 4, and the served label still rides the hover) · the `axis2` register's colouring, never the primary axis's identity colour — the
// register is what tells a scanning clerk which axis they are looking at · the served label on
// hover, single pill included · NEVER abbreviated.
// OVERFLOW (small facts on a row, operator 2026-10-19, round 2 "the stack"): the line shows as many
// values as it has ROOM for ('fit'), a fixed number (a cap), or every value ('all' — the detail
// header never collapses), then one „+n" pill. Hover or focus on „+n" drops the remaining values
// down as the same pills in rank order, in an overlay anchored at the „+n" over the row below; the
// main (first) value stays and nothing in the row moves. The first value is the classifier's order
// — there is no relevance coefficient. Registry: design/design-languages.html, `secondary_axis`.
// WORDING is single-sourced on `cm.secondaryAxisText`; CASING is a register decision and lives
// here. The two surfaces that render an INTERACTIVE second-axis chip from the accessors instead of
// this component (FilterPanel's filter bubble, UploadsSeite's removable reason-code chip) resolve
// the same WORD and route their FACE through `pillFace('axis2', …)` too (walk round 4, 2026-09-08:
// one code is never cased two ways on one screen) — they are still not pills this factory DRAWS
// (own markup, own affordance), they only borrow its voice. Abbreviation belongs to the primary
// axis alone, on the colour strip. A surface with its own affordance should keep reusing the
// resolution accessors + pillFace rather than this component — unifying an interactive chip's
// MARKUP with a display pill would be unification for its own sake.</constraints>
export type SecondaryPillVariant = 'row' | 'stats' | 'detail';

/** How many values show before „+n": a fixed cap, as many as fit the line, or all of them. */
export type SecondaryAxisLimit = number | 'fit' | 'all';

// The variants whose face is CLIPPED rather than shown whole — see VARIANTS below, where the
// clamp itself is declared. Kept as its own set beside the type so the tooltip rule in the
// component reads the FACT (this face may be cut off) rather than re-deriving it from a variant
// name, and so a future clamped variant joins the rule by being added here.
const CLAMPED_VARIANTS: ReadonlySet<SecondaryPillVariant> = new Set(['stats']);

// ── The pill house rule (operator ruling 2026-09-08, walk round 4) ────────────────────────────
// The app draws TWO kinds of pill, and no third:
//   axis2 — the tenant's SECOND classification axis. The resolved label VERBATIM in its served
//           casing (part 7, 2026-10-11 — „TAB/RFZ" must not read „tab/rfz"), on the gray
//           ground, the quieter of the two registers: a row carries several of these at once, so
//           the axis reads as one calm group rather than as several shouted words.
//   info  — everything a pill can say ABOUT a row that is not a classification: a claim state, a
//           badge, a hint, a count. Propercase face on a white ground with a navy-light border —
//           each one is a single statement, and it stands out because it is rarer.
// One factory owns both, and the two token sets live in ONE table below rather than inline in the
// factory — swapping what a register looks like (or exchanging the two) is then an edit to that
// table and nothing else. The registers are deliberately only two: a third would be a look nobody
// could name, which is how the four hand-rolled copies this factory replaced came about.
// Beside the two registers stand the SPLIT PILL (SplitPill.tsx: key | value, the clerk traces and
// the AI descriptors) and the DECISION BADGE (DecisionBadge.tsx: the primary axis's badge shape
// with an icon key) — neither is a register of this factory; both are documented in the registry.
// The shape-borrowers in shared.tsx: StufeChip (info metrics, roman numeral face), ZoneChip (its
// own signal ramp) and ReviewChip (free prose through `pillFace('info', …)`). overlayEditorChrome's
// admin-overlay badge (its own file, its own exported name so the two `pillStyle`s never collide on
// one import line) keeps one further exception on ONE of its two faces: the customization-marker
// face keeps its own filled look for the same "this differs from the served default" signal
// reason; its sibling face is fully `info`-register compliant, face included.
export type PillRegister = 'axis2' | 'info';

interface PillRegisterTokens {
  fill: string;
  text: string;
  border: string;
  /**
   * Whether the register renders the served face verbatim, or raises its FIRST letter only.
   * part 7, 2026-10-11: axis2 used to lowercase the served word („TAB/RFZ" read „tab/rfz") — an
   * operator-ruled defect; the served text now renders exactly as served.
   * Propercase touches nothing but character 0, so a served noun that already arrives Propercase
   * (`Zählerstand – Wert unklar`) passes through byte-identical — the register never rewrites a
   * tenant's wording, it only decides how the first letter sits.
   */
  face: 'served' | 'propercase';
}

const PILL_REGISTERS: Record<PillRegister, PillRegisterTokens> = {
  axis2: {
    fill: theme.colors.bgLight,
    text: theme.colors.navyMid,
    border: theme.colors.borderLight,
    face: 'served',
  },
  info: {
    fill: theme.colors.white,
    text: theme.colors.navyMid,
    border: theme.colors.navyLight,
    face: 'propercase',
  },
};

const BASE: React.CSSProperties = {
  fontWeight: 500, borderRadius: 10,
  whiteSpace: 'nowrap', flexShrink: 0,
};

// Only the metrics differ per surface; colour and shape are fixed, because they are what carry the
// "this is the secondary axis" signal.
// stats' clamp (maxWidth/overflow/textOverflow/boxSizing) is real, not decorative — StatistikSeite
// sizes its reason-code lane to at most 180px (pillWidth caps there at a 24-char face; the longest
// baked gMSB face is exactly 24, so this bites only a served id past roughly 27 chars — reachable by
// VNB anliegen / wim process types) and ellipsizes what doesn't fit, title still carrying the full
// text. It composes cleanly with BASE's `whiteSpace: nowrap` — nowrap forces the text onto one line
// so `text-overflow: ellipsis` has a single line to truncate, rather than fighting it — so do not
// "simplify" one away thinking it's redundant with the other; they are two different jobs each
// clamp direction (BASE = never wrap, stats = clip at the lane's own width) that only compose
// correctly together.
const VARIANTS: Record<SecondaryPillVariant, React.CSSProperties> = {
  row:    { fontSize: 10, padding: '1px 8px' },
  stats:  {
    fontSize: 11, padding: '2px 8px', boxSizing: 'border-box',
    maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
    // display:'inline-block' blockifies this span so maxWidth/overflow/textOverflow can apply at
    // all — a plain inline element ignores them outright. The clamp works today without this
    // because the one 'stats' call site (StatistikSeite) happens to render it as a flex child,
    // which blockifies it incidentally — but that is a fact about the caller, not about 'stats'.
    // The predecessor style object this variant replaced (rcPillStyle) carried this explicitly for
    // exactly that reason, and its removal in an earlier collapse silently made the clamp depend on
    // callers never changing — restoring it here makes 'stats' self-contained again. Do NOT delete
    // as "redundant with the flex parent": the parent is incidental, this is not.
    display: 'inline-block',
  },
  detail: { fontSize: 11, padding: '2px 10px' },
};

/**
 * THE pill style factory: a register (what kind of thing this pill says) x a variant (which
 * surface it sits on). Every plain pill in the app draws from here — the four hand-rolled copies
 * of the same object that preceded it are exactly how a colour change used to drift between
 * surfaces (see the 'stats' comment above for the one that already did).
 */
// THE border width of the info tier's OUTLINED signal pills — FlagPill, SenderPill, FristGenanntPill and
// StufeChip (final review, part 7, 2026-10-11): one width, so pills sharing a row share one height.
export const INFO_PILL_BORDER_WIDTH = '1.5px';

export const pillStyle = (register: PillRegister, variant: SecondaryPillVariant): React.CSSProperties => {
  const tokens = PILL_REGISTERS[register];
  return {
    ...BASE,
    color: tokens.text,
    backgroundColor: tokens.fill,
    border: `1px solid ${tokens.border}`,
    ...VARIANTS[variant],
  };
};

/**
 * THE pill face: the register decides the casing, so no call site spells a cased German word (or a
 * `textTransform`) of its own. An info call site authors its noun as prose and the register raises
 * the first letter — which is why an all-caps literal would defeat it and none is authored. de-DE
 * explicitly — the app's one language, and the locale whose casing rules this text is written under.
 */
export const pillFace = (register: PillRegister, text: string): string =>
  PILL_REGISTERS[register].face === 'served'
    ? text
    : text.charAt(0).toLocaleUpperCase('de-DE') + text.slice(1);

// ── Room-aware fitting ('fit') ──────────────────────────────────────────────────────────────────
// The gap between pills inside the group, and between the group and its neighbours on the line.
const PILL_GAP = 4;

/** The flex items of a container, looking THROUGH `display: contents` wrappers (the row's
 *  classification group is one): those are the boxes that share the line's width. */
const flexItemsOf = (container: Element): HTMLElement[] => {
  const items: HTMLElement[] = [];
  Array.from(container.children).forEach(child => {
    const el = child as HTMLElement;
    if (getComputedStyle(el).display === 'contents') items.push(...flexItemsOf(el));
    else items.push(el);
  });
  return items;
};

/** The nearest ancestor that has a box — the line this group shares its width on. */
const lineOf = (host: HTMLElement): HTMLElement | null => {
  let el = host.parentElement;
  while (el && getComputedStyle(el).display === 'contents') el = el.parentElement;
  return el;
};

/** A sibling's claim on the line: its laid-out width, or its natural width where it is a box that
 *  gives way (the row's info group clips its own tail — its scrollWidth is what it wants). */
const claimOf = (el: HTMLElement): number => Math.max(el.getBoundingClientRect().width, el.scrollWidth || 0);

/** How many of `widths` fit in `room`, with a „+n" pill of `plusWidth` after them whenever some
 *  do not — never fewer than one (the main value always shows), never more than all. Pure, so the
 *  rule is pinned without a layout. */
export const fitCount = (widths: readonly number[], plusWidth: number, room: number, gap: number = PILL_GAP): number => {
  const n = widths.length;
  if (n <= 1) return n;
  const total = widths.reduce((s, w) => s + w, 0) + gap * (n - 1);
  if (total <= room) return n;
  let used = 0;
  let k = 0;
  for (let i = 0; i < n - 1; i++) {
    const next = used + widths[i] + (i > 0 ? gap : 0);
    // Everything shown so far, this pill, and the „+n" that must follow it.
    if (next + gap + plusWidth > room) break;
    used = next;
    k = i + 1;
  }
  return Math.max(1, k);
};

// Fonts settle once per document; a group mounted before that re-measures once they have.
let fontsSettled = typeof document === 'undefined' || !('fonts' in document) || document.fonts.status === 'loaded';
const whenFontsSettle = (cb: () => void) => {
  if (fontsSettled) return;
  document.fonts.ready.then(() => { fontsSettled = true; cb(); });
};

interface FitState {
  /** The faces this measurement is for (the codes AND their rendered words — a vocabulary landing
   *  changes a face's width without changing the code). */
  key: string;
  k: number;
  widths: number[];
  plus: number;
  /** True once a pass has read real widths. A pass on a line without layout leaves this false, so
   *  the next resize tick requests the pass again instead of trusting zero widths. */
  measured: boolean;
  /** A measuring pass is requested (mount, a changed face list, a resize on an unmeasured line,
   *  fonts settling). While a pass is pending the group renders every pill plus the hidden „+n"
   *  measurer; a pass on a line without layout clears it, so no measurer lingers where nothing
   *  can be measured. */
  pending: boolean;
}

/**
 * The 'fit' count for a group: a MEASURING pass renders every pill (and a hidden „+n" the size of
 * the largest possible count) before paint, reads their widths and the line's free room, and
 * settles on the count in the same layout effect — the row never paints the measuring pass. A
 * line that has no measurable width (no layout engine, a hidden pane) shows every value and keeps
 * the pass pending; a later resize runs it. Resizes of the line or of a sibling (a badge growing
 * when its word lands, a trace appearing) recompute from the cached pill widths; a changed face
 * list measures again.
 */
function useFitCount(host: React.RefObject<HTMLElement | null>, facesKey: string, count: number, enabled: boolean): { k: number; measuring: boolean } {
  const [state, setState] = useState<FitState>({ key: '', k: count, widths: [], plus: 0, measured: false, pending: true });
  const measuring = enabled && (state.key !== facesKey || state.pending);

  useLayoutEffect(() => {
    if (!enabled || !measuring) return;
    const el = host.current;
    const line = el ? lineOf(el) : null;
    const room = el && line ? roomOn(line, el) : null;
    if (!el || !line || room === null) {
      // No layout to measure against: show every value; the pass is spent (nothing to measure), the
      // group stays unmeasured so a later resize asks again.
      setState({ key: facesKey, k: count, widths: [], plus: 0, measured: false, pending: false });
      return;
    }
    const pills = Array.from(el.querySelectorAll<HTMLElement>('[data-secondary-axis-measure="pill"]'));
    const plusEl = el.querySelector<HTMLElement>('[data-secondary-axis-measure="plus"]');
    const widths = pills.map(p => p.getBoundingClientRect().width);
    const plus = plusEl ? plusEl.getBoundingClientRect().width : 0;
    setState({ key: facesKey, k: fitCount(widths, plus, room), widths, plus, measured: true, pending: false });
  }, [enabled, measuring, facesKey, count, host]);

  // Resizes of the line or its other items: the room changes, the pills do not — recompute from
  // the cache; an unmeasured group asks for its pass instead.
  useEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return;
    const el = host.current;
    const line = el ? lineOf(el) : null;
    if (!el || !line) return;
    const ro = new ResizeObserver(() => {
      setState(prev => {
        if (!prev.measured) return prev.pending ? prev : { ...prev, pending: true };
        const room = roomOn(line, el);
        const k = room === null ? prev.widths.length : fitCount(prev.widths, prev.plus, room);
        return k === prev.k ? prev : { ...prev, k };
      });
    });
    ro.observe(line);
    flexItemsOf(line).forEach(item => { if (item !== el) ro.observe(item); });
    return () => ro.disconnect();
  }, [enabled, host, state.measured, facesKey]);

  // Web fonts landing after mount change every width: measure once more then (never after unmount).
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    whenFontsSettle(() => { if (live) setState(prev => ({ ...prev, pending: true })); });
    return () => { live = false; };
  }, [enabled]);

  return { k: enabled ? state.k : count, measuring };
}

/** The width the group may take on its line: the line's inner width minus every other item's
 *  claim and the gaps between items. Null when the line has no measurable width. */
const roomOn = (line: HTMLElement, host: HTMLElement): number | null => {
  const inner = line.clientWidth;
  if (!(inner > 0)) return null;
  const gap = parseFloat(getComputedStyle(line).columnGap) || 0;
  const others = flexItemsOf(line).filter(item => item !== host);
  const claimed = others.reduce((s, item) => s + claimOf(item), 0);
  return inner - claimed - gap * others.length;
};

export const SecondaryAxisPills: React.FC<{
  codes: string[];
  maxVisible: SecondaryAxisLimit;
  variant?: SecondaryPillVariant;
}> = ({ codes, maxVisible, variant = 'row' }) => {
  const cm = useCategoryMeta();
  const hostRef = useRef<HTMLSpanElement>(null);
  // Keyed on the FACES, not only the codes: a vocabulary landing turns a de-slugged face into the
  // served label and changes every width while the codes stay the same.
  const fit = useFitCount(hostRef, codes.map(c => `${c}=${cm.secondaryAxisText(c)}`).join('\u0000'), codes.length, maxVisible === 'fit');
  if (codes.length === 0) return null;
  const style = pillStyle('axis2', variant);
  const limit = maxVisible === 'all' ? codes.length : maxVisible === 'fit' ? (fit.measuring ? codes.length : fit.k) : maxVisible;
  const hidden = codes.slice(limit);
  // Whether this variant CLIPS its face. Only the clamped one does, and the clamp is what decides
  // whether a tooltip that repeats the face is redundant or is the only way to read the tail — so
  // the rule keys on the clamp itself, not on a variant name spelled at the return below.
  const clamped = CLAMPED_VARIANTS.has(variant);
  const drawPill = (code: string, measure: boolean) => {
    // Face and hover come from the SAME accessor pair (the face rule itself lives there, see
    // ClassificationSchemaContext). They differ only where the hover still has something to
    // add: an unresolved code (de-slugged face, raw code on hover). When they are the same
    // string the tooltip is OMITTED rather than mounted — a describedby that repeats the
    // visible text tells a screen-reader user nothing twice and adds a hover bubble that
    // announces what is already on screen.
    // The omission is VARIANT-AWARE (re-review of round 4, 2026-09-08). On a CLAMPED variant
    // the face is ellipsized at the lane's width, so a tooltip repeating it is not a repeat at
    // all — it is the only way to read what was clipped. The first cut of the omission rule
    // named this cost and left it; the answer is one condition, not a surface decision.
    const face = cm.secondaryAxisText(code);
    const hover = cm.secondaryAxisHover(code);
    // The testid is on the VALUE pills only (never the +n overflow pill below, which is an
    // affordance rather than a value): it is what lets the row-pill parity guard see this
    // signal on a row, and what „Hinweise in der Liste" tests assert the absence of.
    const pill = <span data-testid="secondary-axis-pill" data-secondary-axis-measure={measure ? 'pill' : undefined} style={style}>{pillFace('axis2', face)}</span>;
    return hover === face && !clamped
      ? <React.Fragment key={code}>{pill}</React.Fragment>
      : <InfoTooltip key={code} text={hover}>{pill}</InfoTooltip>;
  };
  const pills = codes.slice(0, limit).map(code => drawPill(code, fit.measuring));
  // The STACK: hover or focus on „+n" drops the hidden values down as the same pills, in rank
  // order, anchored at the „+n" — the house reveal (InfoTooltip: portal, placement, the delay),
  // with pills instead of prose and no navy chrome. `text` keeps the accessible reading.
  const overflow = hidden.length > 0 && (
    <InfoTooltip
      text={hidden.map(cm.secondaryAxisHover).join(', ')}
      content={(
        <span data-testid="secondary-axis-stack" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: PILL_GAP }}>
          {hidden.map(code => (
            <span key={code} data-testid="secondary-axis-stack-pill" style={{ ...style, boxShadow: theme.shadows.cardHover }}>
              {pillFace('axis2', cm.secondaryAxisText(code))}
            </span>
          ))}
        </span>
      )}
    >
      <span data-testid="secondary-axis-overflow" style={style}>+{hidden.length}</span>
    </InfoTooltip>
  );
  if (maxVisible !== 'fit') return <>{pills}{overflow}</>;
  return (
    <span ref={hostRef} data-testid="secondary-axis-group" style={{ display: 'inline-flex', alignItems: 'center', gap: PILL_GAP, flexShrink: 0, position: 'relative' }}>
      {pills}
      {overflow}
      {fit.measuring && codes.length > 1 && (
        // The widest „+n" this list could need, measured out of flow and never painted.
        <span aria-hidden="true" data-secondary-axis-measure="plus" style={{ ...style, position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}>+{codes.length - 1}</span>
      )}
    </span>
  );
};
